// Service để upload files lên Cloudinary
import {
  CLOUDINARY_CONFIG,
  CLOUDINARY_UPLOAD_URL,
  CLOUDINARY_VIDEO_UPLOAD_URL,
} from '../config/cloudinary';

// Upload một file lên Cloudinary
export const uploadToCloudinary = async (file, folder = 'gk88-charity') => {
  try {
    // Tạo FormData để upload file
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_CONFIG.upload_preset);
    formData.append('folder', folder);
    // Transformation sẽ được cấu hình trong Upload Preset

    // Upload file
    const response = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      success: true,
      url: data.secure_url,
      public_id: data.public_id,
      format: data.format,
      bytes: data.bytes,
    };
  } catch (error) {
    console.error('Error uploading to Cloudinary:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Upload nhiều files cùng lúc với progress tracking
export const uploadMultipleFiles = async (files, folder = 'gk88-charity', onProgress = null) => {
  try {
    let completedCount = 0;
    const totalFiles = files.length;

    // Tạo upload promises với progress tracking
    const uploadPromises = files.map(async (file, index) => {
      try {
        const result = await uploadToCloudinary(file, folder);

        // Cập nhật progress
        completedCount++;
        if (onProgress) {
          onProgress({
            completed: completedCount,
            total: totalFiles,
            percentage: Math.round((completedCount / totalFiles) * 100),
            currentFile: file.name,
            result: result,
          });
        }

        return { ...result, index, fileName: file.name };
      } catch (error) {
        completedCount++;
        if (onProgress) {
          onProgress({
            completed: completedCount,
            total: totalFiles,
            percentage: Math.round((completedCount / totalFiles) * 100),
            currentFile: file.name,
            result: { success: false, error: error.message },
          });
        }
        return { success: false, error: error.message, index, fileName: file.name };
      }
    });

    // Chờ tất cả uploads hoàn thành
    const results = await Promise.all(uploadPromises);

    // Tách thành công và thất bại
    const successful = results.filter((result) => result.success);
    const failed = results.filter((result) => !result.success);

    return {
      success: failed.length === 0,
      successful: successful,
      failed: failed,
      urls: successful.map((result) => result.url),
      totalFiles: totalFiles,
      completedFiles: successful.length,
      failedFiles: failed.length,
    };
  } catch (error) {
    console.error('Error uploading multiple files:', error);
    return {
      success: false,
      error: error.message,
      successful: [],
      failed: [],
      urls: [],
      totalFiles: files.length,
      completedFiles: 0,
      failedFiles: files.length,
    };
  }
};

// Upload hình ảnh với tối ưu hóa
export const uploadImage = async (file, folder = 'gk88-charity/images') => {
  try {
    // Debug: Log cấu hình
    console.log('Cloudinary Config:', {
      cloud_name: CLOUDINARY_CONFIG.cloud_name,
      upload_preset: CLOUDINARY_CONFIG.upload_preset,
      upload_url: CLOUDINARY_UPLOAD_URL,
    });

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_CONFIG.upload_preset);
    formData.append('folder', folder);

    const response = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload error response:', errorText);
      throw new Error(`Image upload failed: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();

    return {
      success: true,
      url: data.secure_url,
      public_id: data.public_id,
      format: data.format,
      bytes: data.bytes,
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Upload video với tối ưu hóa
export const uploadVideo = async (file, folder = 'gk88-charity/videos') => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_CONFIG.upload_preset);
    formData.append('folder', folder);
    formData.append('resource_type', 'video');

    const response = await fetch(CLOUDINARY_VIDEO_UPLOAD_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Video upload failed: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      success: true,
      url: data.secure_url,
      public_id: data.public_id,
      format: data.format,
      bytes: data.bytes,
    };
  } catch (error) {
    console.error('Error uploading video:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

// Upload nhiều images với progress tracking
export const uploadMultipleImages = async (
  files,
  folder = 'gk88-charity/images',
  onProgress = null,
) => {
  try {
    let completedCount = 0;
    const totalFiles = files.length;

    // Tạo upload promises với progress tracking
    const uploadPromises = files.map(async (file, index) => {
      try {
        const result = await uploadImage(file, folder);

        // Cập nhật progress
        completedCount++;
        if (onProgress) {
          onProgress({
            completed: completedCount,
            total: totalFiles,
            percentage: Math.round((completedCount / totalFiles) * 100),
            currentFile: file.name,
            result: result,
          });
        }

        return { ...result, index, fileName: file.name };
      } catch (error) {
        completedCount++;
        if (onProgress) {
          onProgress({
            completed: completedCount,
            total: totalFiles,
            percentage: Math.round((completedCount / totalFiles) * 100),
            currentFile: file.name,
            result: { success: false, error: error.message },
          });
        }
        return { success: false, error: error.message, index, fileName: file.name };
      }
    });

    // Chờ tất cả uploads hoàn thành
    const results = await Promise.all(uploadPromises);

    // Tách thành công và thất bại
    const successful = results.filter((result) => result.success);
    const failed = results.filter((result) => !result.success);

    return {
      success: failed.length === 0,
      successful: successful,
      failed: failed,
      urls: successful.map((result) => result.url),
      totalFiles: totalFiles,
      completedFiles: successful.length,
      failedFiles: failed.length,
    };
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    return {
      success: false,
      error: error.message,
      successful: [],
      failed: [],
      urls: [],
      totalFiles: files.length,
      completedFiles: 0,
      failedFiles: files.length,
    };
  }
};

// Upload nhiều videos với progress tracking
export const uploadMultipleVideos = async (
  files,
  folder = 'gk88-charity/videos',
  onProgress = null,
) => {
  try {
    let completedCount = 0;
    const totalFiles = files.length;

    // Tạo upload promises với progress tracking
    const uploadPromises = files.map(async (file, index) => {
      try {
        const result = await uploadVideo(file, folder);

        // Cập nhật progress
        completedCount++;
        if (onProgress) {
          onProgress({
            completed: completedCount,
            total: totalFiles,
            percentage: Math.round((completedCount / totalFiles) * 100),
            currentFile: file.name,
            result: result,
          });
        }

        return { ...result, index, fileName: file.name };
      } catch (error) {
        completedCount++;
        if (onProgress) {
          onProgress({
            completed: completedCount,
            total: totalFiles,
            percentage: Math.round((completedCount / totalFiles) * 100),
            currentFile: file.name,
            result: { success: false, error: error.message },
          });
        }
        return { success: false, error: error.message, index, fileName: file.name };
      }
    });

    // Chờ tất cả uploads hoàn thành
    const results = await Promise.all(uploadPromises);

    // Tách thành công và thất bại
    const successful = results.filter((result) => result.success);
    const failed = results.filter((result) => !result.success);

    return {
      success: failed.length === 0,
      successful: successful,
      failed: failed,
      urls: successful.map((result) => result.url),
      totalFiles: totalFiles,
      completedFiles: successful.length,
      failedFiles: failed.length,
    };
  } catch (error) {
    console.error('Error uploading multiple videos:', error);
    return {
      success: false,
      error: error.message,
      successful: [],
      failed: [],
      urls: [],
      totalFiles: files.length,
      completedFiles: 0,
      failedFiles: files.length,
    };
  }
};
